"use client";

import { useEffect, useRef, useState } from "react";

// How often a long-lived tab checks for a new deploy. Also re-checks on
// visibilitychange (see below) so a backgrounded tab catches up the moment
// the user comes back, instead of waiting out a full interval.
const POLL_MS = 10 * 60 * 1000;

async function fetchLiveBuild() {
  // cache: "no-store" bypasses the browser's own HTTP cache; the no-store
  // Cache-Control response header (next.config.js) keeps any CDN/edge cache
  // from serving a stale copy of this one path.
  const res = await fetch("/build-info.json", { cache: "no-store" });
  if (!res.ok) return null;
  return res.json();
}

// True once a poll finds a newer build that asks stale tabs to refresh (its
// `refresh` flag, next.config.js); a tab never re-fetches its own JS otherwise.
export function useNewVersionAvailable() {
  const [available, setAvailable] = useState(false);
  // The build this tab actually loaded with — inlined at build time (see
  // next.config.js's BUILD_ID / env.NEXT_PUBLIC_BUILD_ID). Captured once per
  // mount, not per-poll: it can't change without a real page reload.
  const loadedBuildId = useRef(process.env.NEXT_PUBLIC_BUILD_ID).current;

  useEffect(() => {
    // No known build id (non-Next test/dev environment) — nothing to compare.
    if (!loadedBuildId) return undefined;

    let cancelled = false;
    const check = async () => {
      try {
        const live = await fetchLiveBuild();
        if (!cancelled && live?.buildId && live.buildId !== loadedBuildId && live.refresh) {
          setAvailable(true);
        }
      } catch {
        // Offline / transient network error — try again on the next tick.
      }
    };

    check();
    const interval = setInterval(check, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  return available;
}
